import React, { useContext, useEffect, useMemo } from 'react'
import { isEmpty } from 'lodash'

import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import WidgetContainer from '@/components/widget/WidgetContainer'
import WidgetContext from '@/components/widget/widget.context'
import { ExternalCallIdsLink } from '@/views/QACopilot/components/ExternalCallIdsLink'
import { formatTime } from '@/utils/helpers'
import { CALL_DETAIL_PAGE } from '@/reducers/callSearch/callSearch.actions'

const DisputeCountCell = ({ count, callIds, organizationId }) => {
  if (isEmpty(callIds) || count === 0) {
    return { as: <span>0</span>, value: 0 }
  }

  return {
    as: (
      <ExternalCallIdsLink
        callIds={callIds}
        passedOrgId={organizationId}
        label={Number(count).toLocaleString()}
      />
    ),
    value: count,
  }
}

const mergeActivityData = (managerAudit, analystActivity, callViewCounts) => {
  const mergedById = {}

  for (const manager of managerAudit || []) {
    mergedById[manager.manager_id] = {
      manager_id: manager.manager_id,
      first_name: manager.first_name,
      last_name: manager.last_name,
      username: manager.username,
      last_cloud_login: manager.last_cloud_login,
      manual_score_completed: manager.statuses?.manual_score_completed || 0,
    }
  }

  for (const analyst of analystActivity || []) {
    const existing = mergedById[analyst.manager_id] || {
      manager_id: analyst.manager_id,
      first_name: analyst.first_name,
      last_name: analyst.last_name,
      username: analyst.username,
      last_cloud_login: null,
      manual_score_completed: 0,
    }

    mergedById[analyst.manager_id] = {
      ...existing,
      disputes_managed: analyst.disputes_managed,
      disputes_overturned: analyst.disputes_overturned,
      disputes_upheld: analyst.disputes_upheld,
      call_ids: analyst.call_ids,
      overturned_call_ids: analyst.overturned_call_ids,
      upheld_call_ids: analyst.upheld_call_ids,
    }
  }

  for (const viewer of callViewCounts || []) {
    const existing = mergedById[viewer.user_id]

    if (existing) {
      existing.call_views = viewer.views_by_page?.[CALL_DETAIL_PAGE] || 0
    }
  }

  return Object.values(mergedById)
}

const buildTotalsRow = (data, organizationId) => {
  const allCallIds = data.flatMap((row) => row.call_ids || [])
  const allOverturnedCallIds = data.flatMap((row) => row.overturned_call_ids || [])
  const allUpheldCallIds = data.flatMap((row) => row.upheld_call_ids || [])
  const totalManaged = data.reduce((sum, row) => sum + (row.disputes_managed || 0), 0)
  const totalOverturned = data.reduce((sum, row) => sum + (row.disputes_overturned || 0), 0)
  const totalUpheld = data.reduce((sum, row) => sum + (row.disputes_upheld || 0), 0)
  const totalManualScores = data.reduce((sum, row) => sum + (row.manual_score_completed || 0), 0)
  const totalCallViews = data.reduce((sum, row) => sum + (row.call_views || 0), 0)

  return {
    manager_id: 'totals',
    name: 'Totals',
    username: '',
    last_cloud_login: '',
    call_views: totalCallViews,
    manual_score_completed: totalManualScores,
    _pinned: true,
    disputes_managed: DisputeCountCell({
      count: totalManaged,
      callIds: allCallIds,
      organizationId,
    }),
    disputes_overturned: DisputeCountCell({
      count: totalOverturned,
      callIds: allOverturnedCallIds,
      organizationId,
    }),
    disputes_upheld: DisputeCountCell({
      count: totalUpheld,
      callIds: allUpheldCallIds,
      organizationId,
    }),
  }
}

export const QAAnalystActivityWidget = ({
  managerAuditData = [],
  analystActivityData = [],
  callViewCountsData = [],
  loading = false,
  organizationId,
}) => {
  const { handleChangeCsvData } = useContext(WidgetContext)

  const mergedData = useMemo(
    () => mergeActivityData(managerAuditData, analystActivityData, callViewCountsData),
    [managerAuditData, analystActivityData, callViewCountsData]
  )

  const rows = useMemo(
    () =>
      mergedData.map((row) => ({
        ...row,
        name: `${row.first_name} ${row.last_name}`,
        last_cloud_login: row.last_cloud_login
          ? formatTime(row.last_cloud_login, 'MMM D, YYYY h:mm A')
          : '-',
        call_views: row.call_views || 0,
        manual_score_completed: row.manual_score_completed || 0,
        disputes_managed: DisputeCountCell({
          count: row.disputes_managed || 0,
          callIds: row.call_ids,
          organizationId,
        }),
        disputes_overturned: DisputeCountCell({
          count: row.disputes_overturned || 0,
          callIds: row.overturned_call_ids,
          organizationId,
        }),
        disputes_upheld: DisputeCountCell({
          count: row.disputes_upheld || 0,
          callIds: row.upheld_call_ids,
          organizationId,
        }),
      })),
    [mergedData, organizationId]
  )

  const totalsRow = useMemo(
    () => (!isEmpty(mergedData) ? buildTotalsRow(mergedData, organizationId) : null),
    [mergedData, organizationId]
  )

  const allRows = useMemo(() => (totalsRow ? [...rows, totalsRow] : rows), [rows, totalsRow])

  const columns = [
    { accessor: 'name', label: 'Name', isSearchable: true, sticky: true },
    { accessor: 'username', label: 'Email', isSearchable: true },
    { accessor: 'last_cloud_login', label: 'Last Cloud Login' },
    {
      accessor: 'call_views',
      label: 'Call Views',
      headerAlignment: 'right',
    },
    {
      accessor: 'manual_score_completed',
      label: 'Manual Score Completed',
      headerAlignment: 'right',
    },
    {
      accessor: 'disputes_managed',
      label: 'Disputes Managed',
      headerAlignment: 'right',
    },
    {
      accessor: 'disputes_overturned',
      label: 'Disputes Overturned',
      headerAlignment: 'right',
    },
    {
      accessor: 'disputes_upheld',
      label: 'Disputes Upheld',
      headerAlignment: 'right',
    },
  ]

  const csvColumns = [
    { accessor: 'name', label: 'Name' },
    { accessor: 'username', label: 'Email' },
    { accessor: 'last_cloud_login', label: 'Last Cloud Login' },
    {
      accessor: 'call_views',
      label: 'Call Views',
      format: (value) => Number(value).toLocaleString(),
    },
    {
      accessor: 'manual_score_completed',
      label: 'Manual Score Completed',
      format: (value) => Number(value).toLocaleString(),
    },
    {
      accessor: 'disputes_managed',
      label: 'Disputes Managed',
      format: (value) => Number(value).toLocaleString(),
    },
    {
      accessor: 'disputes_overturned',
      label: 'Disputes Overturned',
      format: (value) => Number(value).toLocaleString(),
    },
    {
      accessor: 'disputes_upheld',
      label: 'Disputes Upheld',
      format: (value) => Number(value).toLocaleString(),
    },
  ]

  const csvRows = mergedData.map((row) => ({
    name: `${row.first_name} ${row.last_name}`,
    username: row.username,
    last_cloud_login: row.last_cloud_login
      ? formatTime(row.last_cloud_login, 'MMM D, YYYY h:mm A')
      : '-',
    call_views: row.call_views || 0,
    manual_score_completed: row.manual_score_completed || 0,
    disputes_managed: row.disputes_managed || 0,
    disputes_overturned: row.disputes_overturned || 0,
    disputes_upheld: row.disputes_upheld || 0,
  }))

  useEffect(() => {
    handleChangeCsvData({ headers: csvColumns, data: csvRows })
  }, [managerAuditData, analystActivityData, callViewCountsData])

  return (
    <WidgetContainer
      autoHeight={!loading && !isEmpty(rows)}
      loading={loading}
      noData={isEmpty(rows)}
      withPagination
      table={
        <AdvancedTable
          index="manager_id"
          columns={columns}
          rows={allRows}
          pagination
          rowsPerPage={20}
          defaultOrder="desc"
          defaultOrderBy="manual_score_completed"
          showDescription
          pinned="_pinned"
          pinnedReverse
        />
      }
      chart={<div />}
    />
  )
}
